import {useCallback, useEffect, useMemo, useState} from "react";

/**
 * Generic helpers
 */
function read(storage, key, fallback, isPrivate) {
    return isPrivate
        ? storage.getPrivate(key, fallback)
        : storage.get(key, fallback);
}

function write(storage, key, value, isPrivate) {
    if (isPrivate) {
        storage.setPrivate(key, value);
    } else {
        storage.set(key, value);
    }
}

/**
 * createStorageHook
 *
 * @param {Object} storage - An adapter with get/getPrivate/set/setPrivate.
 * @param {Object} defaults - Default behavior for this type of storage.
 *   - customEventName: string | undefined (e.g. "localStorageChange")
 *   - useWindowStorageEvent: boolean (listen to native cross-tab "storage")
 */
export default function createStorageHook(storage, defaults = {}) {
    const {customEventName, useWindowStorageEvent = false} = defaults;

    /**
     * useStorage
     *
     * @param {string} key
     * @param {*} defaultValue
     * @param {Object} options
     * @param {boolean} [options.isPrivate=false]
     * @param {boolean} [options.sync=false] — if true, keep state in sync with external changes
     */
    return function useStorage(key, defaultValue = null, options = {}) {
        const {isPrivate = false, sync = false} = options;

        const initialValue = useMemo(
            () => read(storage, key, defaultValue, isPrivate),
            [key, defaultValue, isPrivate]
        );

        const [value, setValue] = useState(initialValue);

        const setter = useCallback(
            (next) => {
                const nextValue =
                    typeof next === "function"
                        ? next(read(storage, key, defaultValue, isPrivate))
                        : next;

                write(storage, key, nextValue, isPrivate);

                // If we rely on external events to sync, don't double-set here.
                if (!sync) setValue(nextValue);
            },
            [key, defaultValue, isPrivate, sync]
        );

        useEffect(() => {
            if (!sync) return;

            const handleCustomEvent = (e) => {
                const isPrivateKeyMatch =
                    isPrivate &&
                    typeof e?.key === "string" &&
                    e.key.startsWith("$_");
                const isPublicKeyMatch = e?.key === key;

                if (isPrivateKeyMatch || isPublicKeyMatch) {
                    setValue(read(storage, key, defaultValue, isPrivate));
                }
            };

            const removeFns = [];

            // App-level custom event (e.g., "localStorageChange")
            if (customEventName) {
                document.addEventListener(customEventName, handleCustomEvent);
                removeFns.push(() =>
                    document.removeEventListener(
                        customEventName,
                        handleCustomEvent
                    )
                );
            }

            // Native cross-tab "storage" event (only makes sense for localStorage)
            let nativeHandler;
            if (useWindowStorageEvent) {
                nativeHandler = (e) => {
                    if (e.key === key) {
                        setValue(read(storage, key, defaultValue, isPrivate));
                    }
                };
                window.addEventListener("storage", nativeHandler);
                removeFns.push(() =>
                    window.removeEventListener("storage", nativeHandler)
                );
            }

            return () => {
                removeFns.forEach((fn) => fn());
            };
        }, [sync, key, defaultValue, isPrivate]);

        return [value, setter];
    };
}
